import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { Fragment, useEffect, useMemo, useRef, useState } from 'react';
import DocumentTextIcon from '@heroicons/react/outline/DocumentTextIcon';
import EyeOffIcon from '@heroicons/react/outline/EyeOffIcon';
import dayjs from 'dayjs';
import { Prompt } from 'react-router';
import { hasActionPermission, hasInputPermission, } from '@tribeplatform/gql-client';
import { CustomFieldType, PostMappingTypeEnum, PostStatus, PostTypeContext, } from '@tribeplatform/gql-client/types';
import { useAddPost, useAuthMember, useUpdatePost, } from '@tribeplatform/react-sdk/hooks';
import { Accordion } from '@tribeplatform/react-ui-kit/Accordion';
import { Alert } from '@tribeplatform/react-ui-kit/Alert';
import { Button } from '@tribeplatform/react-ui-kit/Button';
import { List } from '@tribeplatform/react-ui-kit/Layout';
import { toast } from '@tribeplatform/react-ui-kit/Toast';
import { Tooltip } from '@tribeplatform/react-ui-kit/Tooltip';
import { FieldInput, FieldPrivacyBadges, CustomFieldSubtype, } from '../CustomField';
import { getFieldSetting, useFieldPermissions } from '../CustomField/utils';
import { Form } from '../Form';
import { T, useI18n } from '../i18n';
import { PostSeoOptions } from './PostSeoOptions';
import { SimilarPosts } from './SimilarPosts';
import { getTranslatedPostTypeName } from './utils/postTranslate';
export const AdminOnlyInfo = () => (_jsxs(Tooltip, { children: [_jsx(Tooltip.Trigger, { children: _jsx(EyeOffIcon, { className: "w-4 h-4" }, void 0) }, void 0), _jsx(Tooltip.Panel, { children: _jsx(T, { id: "Post.AdminOnlyInfo", defaultMessage: "Only admins and moderators can see and update this section." }, void 0) }, void 0)] }, void 0));
export const PostForm = ({ space, post, postType, onCancel, availableSpaces, onSubmit: onFormSubmit, preview = false, highlightedField, isPostReady, }) => {
    var _a, _b, _c, _d, _e, _f, _g, _h;
    const intl = useI18n();
    const { $t } = intl;
    const formRef = useRef();
    const isFormReady = useRef(false);
    const isNew = !(post === null || post === void 0 ? void 0 : post.id);
    const fields = (_a = postType === null || postType === void 0 ? void 0 : postType.postFields) === null || _a === void 0 ? void 0 : _a.fields;
    const similarQueryKey = 'title';
    const { isAdmin, data: member } = useAuthMember();
    const { canUpdateField } = useFieldPermissions(space);
    const [enablePrompt, setEnablePrompt] = useState(false);
    const [similarQuery, setSimilarQuery] = useState(null);
    const [targetSpace, setTargetSpace] = useState(space);
    const { actionPermission } = hasActionPermission((_c = (_b = post === null || post === void 0 ? void 0 : post.authMemberProps) === null || _b === void 0 ? void 0 : _b.permissions) !== null && _c !== void 0 ? _c : [], 'updatePost');
    const canLock = ((_d = hasInputPermission(actionPermission === null || actionPermission === void 0 ? void 0 : actionPermission.inputPermissions, 'input.locked')) === null || _d === void 0 ? void 0 : _d.authorized) || isAdmin;
    const canSetOwnerId = ((_e = hasInputPermission(actionPermission === null || actionPermission === void 0 ? void 0 : actionPermission.inputPermissions, 'input.ownerId')) === null || _e === void 0 ? void 0 : _e.authorized) || isAdmin;
    const canSetPublishedAt = ((_f = hasInputPermission(actionPermission === null || actionPermission === void 0 ? void 0 : actionPermission.inputPermissions, 'input.publishedAt')) === null || _f === void 0 ? void 0 : _f.authorized) || isAdmin;
    const canSetSlug = ((_g = hasInputPermission(actionPermission === null || actionPermission === void 0 ? void 0 : actionPermission.inputPermissions, 'input.slug')) === null || _g === void 0 ? void 0 : _g.authorized) || isAdmin;
    const { mutateAsync: addPost, isLoading: isAdding } = useAddPost({
        fields: 'basic',
    });
    const { mutateAsync: updatePost, isLoading: isUpdating } = useUpdatePost({
        fields: 'basic',
    });
    useEffect(() => {
        setTargetSpace(space);
    }, [space]);
    const onSubmit = async ({ fields: postFields, customSeoDetail, owner, publishedAt: rawPublishedAt, ...data }, { setError }) => {
        var _a, _b, _c, _d, _e, _f, _g;
        try {
            const mappingFields = [];
            const tagNames = ((_a = data === null || data === void 0 ? void 0 : data.tags) === null || _a === void 0 ? void 0 : _a.map(tag => tag === null || tag === void 0 ? void 0 : tag.title)) || [];
            const spaceId = targetSpace === null || targetSpace === void 0 ? void 0 : targetSpace.id;
            const ownerId = (owner === null || owner === void 0 ? void 0 : owner.id) && canSetOwnerId ? owner === null || owner === void 0 ? void 0 : owner.id : undefined;
            const publish = typeof (data === null || data === void 0 ? void 0 : data.publish) === 'undefined' ? true : data.publish;
            const publishedAt = rawPublishedAt === '' ? null : rawPublishedAt;
            data === null || data === void 0 ? true : delete data.targetSpace;
            data === null || data === void 0 ? true : delete data.tags;
            data === null || data === void 0 ? true : delete data.owner;
            data.customSeoDetail = {
                thumbnailId: (_b = customSeoDetail === null || customSeoDetail === void 0 ? void 0 : customSeoDetail.thumbnail) === null || _b === void 0 ? void 0 : _b.id,
                noIndex: customSeoDetail === null || customSeoDetail === void 0 ? void 0 : customSeoDetail.noIndex,
                description: customSeoDetail === null || customSeoDetail === void 0 ? void 0 : customSeoDetail.description,
                title: customSeoDetail === null || customSeoDetail === void 0 ? void 0 : customSeoDetail.title,
                canonicalUrl: (customSeoDetail === null || customSeoDetail === void 0 ? void 0 : customSeoDetail.canonicalUrl) || null,
            };
            if (!spaceId) {
                return setError(`targetSpace`, {
                    message: $t({
                        id: 'Post.Error.NoSpaceSelected',
                        defaultMessage: 'Please select a {SPACE}',
                    }),
                    type: 'validate',
                });
            }
            Object.keys(postFields).forEach(key => {
                var _a, _b, _c, _d, _e;
                let value = (_a = postFields[key]) !== null && _a !== void 0 ? _a : null;
                const field = fields === null || fields === void 0 ? void 0 : fields.find(field => field.key === key);
                if ((field === null || field === void 0 ? void 0 : field.type) === CustomFieldType.relation) {
                    value = (value === null || value === void 0 ? void 0 : value.id)
                        ? {
                            id: value === null || value === void 0 ? void 0 : value.id,
                            relation: (_b = field === null || field === void 0 ? void 0 : field.typeOptions) === null || _b === void 0 ? void 0 : _b.relationType,
                        }
                        : null;
                }
                else if ((field === null || field === void 0 ? void 0 : field.type) === CustomFieldType.array &&
                    ((_c = field === null || field === void 0 ? void 0 : field.items) === null || _c === void 0 ? void 0 : _c.type) === CustomFieldType.relation) {
                    value =
                        (value === null || value === void 0 ? void 0 : value.map(item => {
                            var _a, _b;
                            return ({
                                id: item === null || item === void 0 ? void 0 : item.id,
                                relation: (_b = (_a = field === null || field === void 0 ? void 0 : field.items) === null || _a === void 0 ? void 0 : _a.typeOptions) === null || _b === void 0 ? void 0 : _b.relationType,
                            });
                        })) || [];
                }
                else if ((field === null || field === void 0 ? void 0 : field.type) === CustomFieldType.date) {
                    if (value === '') {
                        value = null;
                    }
                    if (value) {
                        value = (_e = (_d = new Date(value).toISOString()) === null || _d === void 0 ? void 0 : _d.split('.')) === null || _e === void 0 ? void 0 : _e[0];
                    }
                }
                else if ((field === null || field === void 0 ? void 0 : field.type) === CustomFieldType.text ||
                    (field === null || field === void 0 ? void 0 : field.type) === CustomFieldType.number) {
                    if (value === '') {
                        value = null;
                    }
                }
                if (typeof value !== undefined) {
                    mappingFields.push({
                        key,
                        value: JSON.stringify(value),
                        type: PostMappingTypeEnum.text,
                    });
                }
            });
            let newPost;
            setEnablePrompt(false);
            if (isNew) {
                newPost = await addPost({
                    spaceId,
                    input: {
                        ...data,
                        publish,
                        tagNames,
                        ownerId,
                        postTypeId: postType === null || postType === void 0 ? void 0 : postType.id,
                        mappingFields,
                        publishedAt,
                    },
                });
            }
            else {
                newPost = await updatePost({
                    id: post === null || post === void 0 ? void 0 : post.id,
                    input: {
                        ...data,
                        mappingFields,
                        tagNames,
                        ownerId,
                        publish,
                        publishedAt,
                    },
                });
            }
            if (newPost.status === PostStatus.BLOCKED) {
                toast({
                    title: $t({
                        id: 'Generics.PendingReview',
                        defaultMessage: 'Pending review',
                    }),
                    description: $t({
                        id: 'Post.PendingReview.Description',
                        defaultMessage: 'Your post will be visible to others once it’s been reviewed by a moderator.',
                    }),
                    status: 'info',
                });
            }
            onFormSubmit(newPost);
        }
        catch (error) {
            let validationError = false;
            (_d = (_c = error === null || error === void 0 ? void 0 : error.response) === null || _c === void 0 ? void 0 : _c.errors) === null || _d === void 0 ? void 0 : _d.forEach(error => {
                if (error === null || error === void 0 ? void 0 : error.field) {
                    // If it's a field error, add `fields.` prefix to the field name
                    setError(fields.find(field => (field === null || field === void 0 ? void 0 : field.key) === error.field)
                        ? `fields.${error.field}`
                        : error.field, {
                        message: error === null || error === void 0 ? void 0 : error.message,
                        type: 'validate',
                    });
                    validationError = true;
                }
            });
            if (!validationError) {
                const message = (_g = (_f = (_e = error === null || error === void 0 ? void 0 : error.response) === null || _e === void 0 ? void 0 : _e.errors) === null || _f === void 0 ? void 0 : _f.map(e => e.message)) === null || _g === void 0 ? void 0 : _g.join('\n');
                toast({
                    title: $t({
                        id: 'Generics.Error',
                        defaultMessage: 'Error',
                    }),
                    description: message ||
                        $t({
                            id: 'Generics.SomethingWentWrongTryAgain',
                            defaultMessage: 'Something went wrong, please try again.',
                        }),
                    status: 'error',
                });
            }
        }
    };
    const defaultValues = useMemo(() => {
        var _a, _b, _c, _d;
        return {
            customSeoDetail: post === null || post === void 0 ? void 0 : post.customSeoDetail,
            tags: (post === null || post === void 0 ? void 0 : post.tags) || [],
            locked: canLock ? post === null || post === void 0 ? void 0 : post.locked : undefined,
            publishedAt: canSetPublishedAt
                ? (_b = (_a = post === null || post === void 0 ? void 0 : post.publishedAt) === null || _a === void 0 ? void 0 : _a.split('.')) === null || _b === void 0 ? void 0 : _b[0]
                : undefined,
            owner: canSetOwnerId ? ((_c = post === null || post === void 0 ? void 0 : post.owner) === null || _c === void 0 ? void 0 : _c.member) || member : undefined,
            ...(canSetSlug ? { slug: post === null || post === void 0 ? void 0 : post.slug } : {}),
            fields: ((_d = post === null || post === void 0 ? void 0 : post.fields) === null || _d === void 0 ? void 0 : _d.reduce((result, cur) => {
                var _a;
                const postTypeField = fields === null || fields === void 0 ? void 0 : fields.find(field => field.key === (cur === null || cur === void 0 ? void 0 : cur.key));
                try {
                    let value = JSON.parse(cur.value);
                    // If it's a relationship replace the value with the entities
                    if (((postTypeField === null || postTypeField === void 0 ? void 0 : postTypeField.type) === CustomFieldType.relation ||
                        ((_a = postTypeField === null || postTypeField === void 0 ? void 0 : postTypeField.items) === null || _a === void 0 ? void 0 : _a.type) === CustomFieldType.relation) &&
                        (cur === null || cur === void 0 ? void 0 : cur.relationEntities)) {
                        const flatRelationEntities = Object.keys(cur === null || cur === void 0 ? void 0 : cur.relationEntities).reduce((result, key) => {
                            var _a, _b;
                            return [
                                ...result,
                                ...(Array.isArray((_a = cur === null || cur === void 0 ? void 0 : cur.relationEntities) === null || _a === void 0 ? void 0 : _a[key])
                                    ? (_b = cur === null || cur === void 0 ? void 0 : cur.relationEntities) === null || _b === void 0 ? void 0 : _b[key]
                                    : []),
                            ];
                        }, []);
                        if (Array.isArray(value)) {
                            value = flatRelationEntities;
                        }
                        else {
                            value = flatRelationEntities === null || flatRelationEntities === void 0 ? void 0 : flatRelationEntities[0];
                        }
                    }
                    if (postTypeField.type === CustomFieldType.date && value) {
                        const subtype = getFieldSetting(postTypeField, 'subtype');
                        if (subtype === CustomFieldSubtype.DATETIME) {
                            value = dayjs.utc(value).tz().format('YYYY-MM-DDTHH:mm');
                        }
                        else if (subtype === CustomFieldSubtype.DATE) {
                            value = dayjs.utc(value).tz().format('YYYY-MM-DD');
                        }
                    }
                    result[cur.key] = value;
                }
                catch (e) {
                    console.error('Error parsing member field value', e.message);
                }
                return result;
            }, {})) || {},
        };
    }, [
        canLock,
        canSetOwnerId,
        canSetPublishedAt,
        fields,
        member,
        post === null || post === void 0 ? void 0 : post.customSeoDetail,
        post === null || post === void 0 ? void 0 : post.fields,
        post === null || post === void 0 ? void 0 : post.locked,
        (_h = post === null || post === void 0 ? void 0 : post.owner) === null || _h === void 0 ? void 0 : _h.member,
        post === null || post === void 0 ? void 0 : post.publishedAt,
        post === null || post === void 0 ? void 0 : post.slug,
        post === null || post === void 0 ? void 0 : post.tags,
    ]);
    useEffect(() => {
        var _a;
        if (((_a = formRef.current) === null || _a === void 0 ? void 0 : _a.methods) && isPostReady && !isFormReady.current) {
            formRef.current.methods.reset(defaultValues);
            isFormReady.current = true;
        }
    }, [defaultValues, isPostReady]);
    return (_jsx(Form, { ref: formRef, defaultValues: defaultValues, onChange: () => {
            if (!preview) {
                setEnablePrompt(true);
            }
        }, onSubmit: onSubmit, children: ({ _handleSubmit, _setError }) => {
            return (_jsxs(_Fragment, { children: [_jsx(Prompt, { when: enablePrompt, message: () => $t({
                            id: 'Post.Alert.UnsavedChanges',
                            defaultMessage: 'You have unsaved changes. Are you sure you want to leave this page?',
                        }) }, void 0), _jsxs(List, { children: [(post === null || post === void 0 ? void 0 : post.status) === PostStatus.DRAFTED && (_jsx(List.Item, { children: _jsx(Alert, { status: "neutral", icon: _jsx(DocumentTextIcon, {}, void 0), title: $t({
                                        id: 'Post.Alert.IsDraft',
                                        defaultMessage: 'This is a draft post',
                                    }), withClose: true, children: _jsx(T, { id: "Post.Alert.IsDraft.Description", defaultMessage: "Drafts posts are not visible to other {MEMBERS}" }, void 0) }, void 0) }, void 0)), isNew && (availableSpaces === null || availableSpaces === void 0 ? void 0 : availableSpaces.length) > 1 && (_jsx(Form.SpacePicker, { label: $t({
                                    id: 'Post.PickSpace',
                                    defaultMessage: 'Post in',
                                }), onChange: space => {
                                    if (space === null || space === void 0 ? void 0 : space.id) {
                                        setTargetSpace(space);
                                    }
                                }, name: "targetSpace", value: targetSpace, options: availableSpaces }, void 0)), fields === null || fields === void 0 ? void 0 : fields.map((field, index) => {
                                if ((field === null || field === void 0 ? void 0 : field.archived) ||
                                    !canUpdateField({ field, entity: post })) {
                                    return null;
                                }
                                const placeholder = getFieldSetting(field, 'placeholder');
                                const helperText = getFieldSetting(field, 'helperText');
                                const isSimilarPostField = (field === null || field === void 0 ? void 0 : field.key) === similarQueryKey;
                                const similarPosts = isSimilarPostField && !(post === null || post === void 0 ? void 0 : post.id) ? (_jsx(List.Item, { children: _jsx(SimilarPosts, { query: similarQuery || null, spaceId: targetSpace === null || targetSpace === void 0 ? void 0 : targetSpace.id }, void 0) }, void 0)) : null;
                                return (_jsxs(Fragment, { children: [_jsx(List.Item, { className: highlightedField === (field === null || field === void 0 ? void 0 : field.key) &&
                                                'ring-2 rounded-sm ring-offset-4 ring-offset-surface ring-focused', children: _jsx(FieldInput, { name: `fields.${field.key}`, field: field, label: _jsxs("div", { className: "flex space-s-1 items-center", children: [_jsx("span", { children: getTranslatedPostTypeName(intl, field.name) }, void 0), _jsx(FieldPrivacyBadges, { field: field }, field === null || field === void 0 ? void 0 : field.key)] }, void 0), helperText: helperText, placeholder: placeholder, autoFocus: index === 0 && !preview, onChange: isSimilarPostField
                                                    ? value => setSimilarQuery(value)
                                                    : null }, void 0) }, void 0), similarPosts] }, field.key));
                            }), postType.context === PostTypeContext.post && (_jsx(List.Item, { children: _jsx(Form.TagPicker, { label: $t({
                                        id: 'Generics.Tags',
                                        defaultMessage: '{TAGS_CC}',
                                    }), name: "tags", creatable: true, placeholder: $t({
                                        id: 'Post.AddTags.Placeholder',
                                        defaultMessage: 'Add {TAGS}...',
                                    }), multiple: true }, void 0) }, void 0)), isAdmin && postType.context === PostTypeContext.post && (_jsx(List.Item, { children: _jsxs(Accordion, { variant: "plus", iconAlign: "trailing", children: [_jsx(Accordion.Button, { children: _jsxs("div", { className: "flex space-s-1 items-center", children: [_jsx("span", { children: _jsx(T, { id: "Post.SeoOptions", defaultMessage: "SEO options" }, void 0) }, void 0), _jsx(AdminOnlyInfo, {}, void 0)] }, void 0) }, void 0), _jsx(Accordion.Panel, { children: _jsx(PostSeoOptions, {}, void 0) }, void 0)] }, void 0) }, void 0)), (canLock || canSetOwnerId || canSetPublishedAt) && (_jsx(List.Item, { children: _jsxs(Accordion, { variant: "plus", iconAlign: "trailing", defaultOpen: post === null || post === void 0 ? void 0 : post.locked, children: [_jsx(Accordion.Button, { children: _jsxs("div", { className: "flex space-s-1 items-center", children: [_jsx("span", { children: _jsx(T, { id: "Generics.AdvancedOptions", defaultMessage: "Advanced options" }, void 0) }, void 0), _jsx(AdminOnlyInfo, {}, void 0)] }, void 0) }, void 0), _jsx(Accordion.Panel, { children: _jsxs(List, { children: [canLock && (_jsx(List.Item, { children: _jsx(Form.Toggle, { name: "locked", label: $t({
                                                                id: 'Post.Lock',
                                                                defaultMessage: 'Lock post',
                                                            }), helperText: $t({
                                                                id: 'Post.Lock.Helper',
                                                                defaultMessage: '{MEMBERS} cannot comment or react to locked posts.',
                                                            }) }, void 0) }, void 0)), canSetOwnerId && (_jsx(List.Item, { children: _jsx(Form.MemberPicker, { name: "owner", label: $t({
                                                                id: 'Generics.Author',
                                                                defaultMessage: 'Author',
                                                            }), helperText: $t({
                                                                id: 'Post.Author.Helper',
                                                                defaultMessage: 'You can post on behalf of another {MEMBER}. Please make sure the author is aware of this.',
                                                            }) }, void 0) }, void 0)), canSetPublishedAt && (_jsx(List.Item, { children: _jsx(Form.DateTime, { type: "datetime-local", name: "publishedAt", label: $t({
                                                                id: 'Generics.PublishTime',
                                                                defaultMessage: 'Publish time',
                                                            }), helperText: $t({
                                                                id: 'Post.PublishTime.Helper',
                                                                defaultMessage: 'Manually set the displayed publish date of this post in UTC.',
                                                            }) }, void 0) }, void 0))] }, void 0) }, void 0)] }, void 0) }, void 0)), _jsx(List.Item, { children: _jsxs(Form.Actions, { children: [(post === null || post === void 0 ? void 0 : post.status) !== PostStatus.PUBLISHED ? (_jsx("div", { className: "flex", children: _jsx(Button, { size: "lg", type: "submit", loading: isAdding || isUpdating, disabled: preview, children: _jsx(T, { id: "Generics.Publish", defaultMessage: "Publish" }, void 0) }, void 0) }, void 0)) : (_jsx(Button, { size: "lg", type: "submit", loading: isAdding || isUpdating, disabled: preview || !isFormReady.current, children: _jsx(T, { id: "Generics.Update", defaultMessage: "Update" }, void 0) }, void 0)), !!onCancel && (_jsx(Button, { size: "lg", variant: "outline", onClick: () => onCancel(), disabled: preview, children: _jsx(T, { id: "Generics.Cancel", defaultMessage: "Cancel" }, void 0) }, void 0))] }, void 0) }, void 0)] }, void 0)] }, void 0));
        } }, void 0));
};
//# sourceMappingURL=PostForm.js.map